/**
 * WCAG contrast helpers for branding / theme colors.
 */

function parseHex(hex) {
  const h = hex.replace('#', '');
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function toHex(r, g, b) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${((1 << 24) + (c(r) << 16) + (c(g) << 8) + c(b)).toString(16).slice(1)}`;
}

export function mixHex(hexA, hexB, t) {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  if (!a || !b) return hexA;
  return toHex(
    a.r + (b.r - a.r) * t,
    a.g + (b.g - a.g) * t,
    a.b + (b.b - a.b) * t
  );
}

/** Ensures #rrggbb for HTML color inputs and storage; supports 3-digit hex. */
export function normalizeHex(input, fallback = '#33475b') {
  if (input == null || typeof input !== 'string') return fallback.toLowerCase();
  let s = input.trim();
  if (s === '') return fallback.toLowerCase();
  if (!s.startsWith('#')) s = `#${s}`;
  const h = s.slice(1);
  if (/^[0-9A-Fa-f]{3}$/.test(h)) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(h)) {
    return `#${h.toLowerCase()}`;
  }
  return fallback.toLowerCase();
}

export function relativeLuminance(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const R = lin(rgb.r);
  const G = lin(rgb.g);
  const B = lin(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export function contrastRatio(hexFg, hexBg) {
  const L1 = relativeLuminance(hexFg) + 0.05;
  const L2 = relativeLuminance(hexBg) + 0.05;
  return L1 > L2 ? L1 / L2 : L2 / L1;
}

/** Pick #ffffff or #1a1a1a depending on which reads better on bg. */
export function readableTextOnBackground(bgHex) {
  const white = '#ffffff';
  const nearBlack = '#1a1a1a';
  const bg = normalizeHex(bgHex, '#001f3f');
  const cw = contrastRatio(white, bg);
  const cb = contrastRatio(nearBlack, bg);
  return cw >= cb ? white : nearBlack;
}

/** Muted sidebar text: slightly transparent feel via mix toward bg */
export function mutedSidebarTextOnBackground(bgHex) {
  const main = readableTextOnBackground(bgHex);
  return mixHex(main, bgHex, 0.25);
}

export function defaultMainTextForTheme(isDark) {
  return isDark ? '#f3f4f6' : '#1a1a1a';
}

export function mainContentBackgroundForTheme(isDark) {
  return isDark ? '#0a0a0a' : '#ffffff';
}

/**
 * Adjust foreground toward black or white until contrast vs background >= minRatio.
 */
export function ensureContrastOnBackground(fgHex, bgHex, minRatio = 4.5) {
  const fg = normalizeHex(fgHex, '#1a1a1a');
  const bg = normalizeHex(bgHex, '#ffffff');
  if (contrastRatio(fg, bg) >= minRatio) return fg;
  const lumBg = relativeLuminance(bg);
  const toward = lumBg > 0.179 ? '#000000' : '#ffffff';
  let best = fg;
  for (let i = 1; i <= 24; i++) {
    const t = i / 24;
    const mixed = mixHex(fg, toward, t);
    best = mixed;
    if (contrastRatio(mixed, bg) >= minRatio) return mixed;
  }
  return best;
}
